import React, { useState, useCallback, useEffect, useRef } from 'react'
import Image from '../Image'
import { Image as ImageType } from 'src/apollo/sites'
import { t } from 'src/translations'
import { getImageUrl } from '../Image'
import { AddCircleIcon } from '@toasttab/buffet-pui-icons'
import { useEditor } from '@toasttab/sites-components'
import { ChevronLeftIcon } from '@toasttab/buffet-pui-icons'
import { ChevronRightIcon } from '@toasttab/buffet-pui-icons'
type CarouselV2Props = {
  editPath: string
  images?: ImageType[]
  options?: {
    showArrows?: boolean
    showSequenceDots?: boolean
    loop?: boolean
    autoPlayOn?: boolean
    autoPlayTime?: number
    dotShape?: 'circles' | 'pill-active' | 'bar'
    dotOutline?: boolean
    dotNumbers?: boolean
  }
  style?: React.CSSProperties
}
/**
 * This is an editable carousel that users can configure all aspects of. They can upload their images,
 * change the styling of it, change the autoscroll settings, etc. It currently only used in Blocks.tsx
 * as a draggable block that interfaces with dynamic sections.
 * @param editPath - The path to the carousel in the site schema.
 * @param images - The images to display in the carousel. derived from the site schema
 * @param options - The options to configure the carousel. derived from the site schema
 * @param style - The style to apply to the carousel.
 * @returns EditableCarousel component that allows users to configure all aspects of the carousel.
 */
export const CarouselV2 = ({
  editPath,
  images = [],
  options = {},
  style
}: CarouselV2Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { useEditableRef, isEditor } = useEditor()

  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'carousel',
    displayName: 'Carousel',
    actions: ['delete'],
    path: editPath,
    schema: { fields: [] }
  })

  const imagesExist = images && images.length > 0
  const showCarouselPlaceholder = !imagesExist && isEditor

  const {
    showArrows = true,
    showSequenceDots = true,
    loop = true,
    autoPlayOn = false,
    autoPlayTime = 1,
    dotShape = 'circles',
    dotOutline = false,
    dotNumbers = false
  } = options
  const transitionDuration = 300 // transition animation between carousel images
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const goToNext = useCallback(
    (
      e?:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>
    ) => {
      e?.stopPropagation()
      if (isTransitioning) return

      setIsTransitioning(true)
      const nextIndex = loop
        ? (currentIndex + 1) % images.length
        : Math.min(currentIndex + 1, images.length - 1)

      setCurrentIndex(nextIndex)
      setTimeout(() => setIsTransitioning(false), transitionDuration)
    },
    [currentIndex, images.length, loop, isTransitioning, transitionDuration]
  )

  const goToPrevious = useCallback(
    (
      e?:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>
    ) => {
      e?.stopPropagation()
      if (isTransitioning) return

      setIsTransitioning(true)
      const prevIndex = loop
        ? (currentIndex - 1 + images.length) % images.length
        : Math.max(currentIndex - 1, 0)

      setCurrentIndex(prevIndex)
      setTimeout(() => setIsTransitioning(false), transitionDuration)
    },
    [currentIndex, images.length, loop, isTransitioning, transitionDuration]
  )

  const goToSlide = useCallback(
    (
      index: number,
      e?:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>
    ) => {
      e?.stopPropagation()
      if (isTransitioning || index === currentIndex) return

      setIsTransitioning(true)
      setCurrentIndex(index)
      setTimeout(() => setIsTransitioning(false), transitionDuration)
    },
    [currentIndex, isTransitioning, transitionDuration]
  )

  const touchStartXRef = useRef<number | null>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX || null
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const startX = touchStartXRef.current
      const endX = e.changedTouches[0]?.clientX

      if (startX === null) return

      const diff = startX - (endX || 0)

      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          goToNext()
        } else {
          goToPrevious()
        }
      }

      touchStartXRef.current = null // Reset
    },
    [goToNext, goToPrevious]
  )

  useEffect(() => {
    if (!autoPlayOn || images.length <= 1) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return
    }

    const delay = Math.max(autoPlayTime, 1) * 1000 - transitionDuration

    timeoutRef.current = setTimeout(() => {
      goToNext()
    }, delay)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [autoPlayOn, autoPlayTime, images.length, currentIndex, goToNext])

  if (showCarouselPlaceholder) {
    return (
      <CarouselPlaceholder
        style={style}
        editableRef={editableRef}
        editPath={editPath}
      />
    )
  }
  if (!imagesExist) {
    return null
  }

  return (
    <div className='carouselV2-container' style={style}>
      {/* anchor used for floating editor wrapper */}
      <div
        id={editPath}
        style={{ position: 'absolute', top: 0, right: 0 }}
      ></div>
      <div
        className='carouselV2'
        ref={editableRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        data-testid='carousel-container'
      >
        <div
          className='carousel-items'
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: isTransitioning
              ? `transform ${transitionDuration}ms ease-in-out`
              : 'none'
          }}
        >
          {images.map((image, index) => (
            <div key={index} className='carousel-item'>
              <Image
                imageObject={image}
                src={image.src}
                alt={
                  image.altText ||
                  t('carousel.image-alt', { imageNumber: index + 1 })
                }
                fit={image.fit}
              />
            </div>
          ))}
        </div>

        {showArrows && images.length > 1 && (
          <div className='carousel-arrows'>
            <button
              onClick={goToPrevious}
              disabled={!loop && currentIndex === 0}
              className={`arrow-btn prev ${
                !loop && currentIndex === 0 ? 'disabled' : ''
              }`}
              data-editable-click-passthrough='true'
              data-testid='carousel-left-button'
              aria-label={t('carousel.go-to-previous')}
            >
              <ChevronLeftIcon accessibility='decorative' />
            </button>
            <button
              onClick={goToNext}
              disabled={!loop && currentIndex === images.length - 1}
              className={`arrow-btn next ${
                !loop && currentIndex === images.length - 1 ? 'disabled' : ''
              }`}
              data-editable-click-passthrough='true'
              data-testid='carousel-right-button'
              aria-label={t('carousel.go-to-next')}
            >
              <ChevronRightIcon accessibility='decorative' />
            </button>
          </div>
        )}

        {showSequenceDots && images.length > 1 && (
          <div
            className='carousel-dots'
            data-testid='carousel-dots'
            data-dot-shape={dotShape}
            data-dot-outline={dotOutline}
            data-dot-numbers={dotNumbers}
          >
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => goToSlide(index, e)}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                aria-label={t('carousel.go-to-slide', {
                  slideNumber: index + 1
                })}
                data-editable-click-passthrough='true'
              >
                <span>{index + 1}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const CarouselPlaceholder = ({
  style,
  editableRef,
  editPath
}: {
  style?: React.CSSProperties
  editableRef: React.RefObject<HTMLDivElement>
  editPath: string
}) => {
  return (
    <div
      className='editableCarousel placeholder'
      ref={editableRef}
      style={style}
      data-testid='carousel-placeholder'
    >
      <div id={editPath} className='absolute top-0 right-0'></div>
      <div className='placeholder-icon'>
        <div className='default-icon'>
          <img
            src={getImageUrl('admin/icons/carousel-placeholder.svg')}
            alt={t('carousel.placeholder-alt')}
          />
        </div>
        <div className='hover-icon'>
          <AddCircleIcon accessibility='decorative' />
        </div>
      </div>
      <div className='placeholder-text'>
        <p>{t('carousel.click-to-add-images')}</p>
      </div>
    </div>
  )
}
